// frontend/src/components/ExpectedWeatherCard.jsx
// Project: Vision ANPR - Expected Weather Card (reusable)
// Author: Castle Traffic Ltd
// Created: 2026-03-05
// Updated: 2026-09-16
//
// Description:
//   Compact daily forecast card for a job period.
//   Uses /api/weather/spot?period=daily&start_date=YYYY-MM-DD&days=N
//
// Critical fix:
//   Backend returns a wider window in `series` (may start BEFORE start_date).
//   UI MUST filter `series` by the requested job date range (start..end) using YYYY-MM-DD keys.
//
// IMPORTANT:
//   Job scheduled_start/end often come from Postgres timestamps like "YYYY-MM-DD HH:MM:SS".
//   Firefox treats that format as Invalid Date. We normalize before parsing.
//
// Mobile responsiveness (2026-09-16):
//   - Header stacks cleanly on narrow screens.
//   - Long location/date text may wrap instead of forcing horizontal overflow.
//   - Forecast rows retain all weather detail on mobile rather than truncating it.
//   - Temperature remains visible while summary/gust/precipitation text wraps naturally.
//   - No data-fetching, date filtering, or weather-code behaviour changed.

import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../api/axiosInstance";

// ---------- helpers ----------
const pad2 = (n) => String(n).padStart(2, "0");

const normalizeDateInput = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v).trim();
  if (!s) return "";

  // Postgres style: "2026-03-27 09:00:00" -> "2026-03-27T09:00:00"
  if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(s)) {
    return s.replace(" ", "T");
  }
  return s;
};

const safeDate = (v) => {
  const n = normalizeDateInput(v);
  if (!n) return null;

  // If it is just YYYY-MM-DD, make it explicit local midnight
  if (/^\d{4}-\d{2}-\d{2}$/.test(n)) {
    const d = new Date(`${n}T00:00:00`);
    return isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(n);
  return isNaN(d.getTime()) ? null : d;
};

const ymdFromAny = (v) => {
  const d = safeDate(v);
  if (!d) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const addDaysYmd = (ymd, addDays) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(ymd || ""))) return "";
  const d = new Date(`${ymd}T00:00:00`);
  if (isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + Number(addDays || 0));
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const daysInclusive = (startAny, endAny) => {
  const s = safeDate(startAny);
  const e = safeDate(endAny);
  if (!s || !e) return 1;

  const s0 = new Date(s.getFullYear(), s.getMonth(), s.getDate());
  const e0 = new Date(e.getFullYear(), e.getMonth(), e.getDate());
  const diff = Math.floor((e0.getTime() - s0.getTime()) / (24 * 60 * 60 * 1000));
  const inc = diff + 1;
  if (!Number.isFinite(inc) || inc <= 0) return 1;
  return Math.min(Math.max(inc, 1), 60); // clamp to 1..60
};

const extractHttpError = (e) => {
  const r = e?.response;
  if (!r) return e?.message || String(e);
  const d = r.data;
  const detail =
    (d && (typeof d.detail === "string" ? d.detail : JSON.stringify(d.detail))) ||
    (typeof d === "string" ? d : JSON.stringify(d));
  return `${r.status} ${r.statusText || ""} - ${detail}`.trim();
};

const fmtDay = (ymdOrIso) => {
  const s = String(ymdOrIso || "");
  const ymd = /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : s.slice(0, 10);
  const d = safeDate(ymd);
  if (!d) return ymd || String(ymdOrIso || "");
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" });
};

const mpsToMph = (mps) => {
  const n = Number(mps);
  if (!Number.isFinite(n)) return null;
  return n * 2.2369362920544;
};

// ---------- Weather code mapping ----------
// Supports both Open-Meteo/WMO style codes and your existing Met Office-ish codes.
// Open-Meteo uses WMO weather interpretation codes, so do NOT let raw numbers leak to UI.
const labelForWeatherCode = (code) => {
  const c = Number(code);
  if (!Number.isFinite(c)) return "";

  const exactMap = {
    // Open-Meteo / WMO
    0: "Clear sky",
    1: "Mainly clear",
    2: "Partly cloudy",
    3: "Overcast",
    45: "Fog",
    48: "Rime fog",
    51: "Light drizzle",
    53: "Moderate drizzle",
    55: "Dense drizzle",
    56: "Light freezing drizzle",
    57: "Dense freezing drizzle",
    61: "Slight rain",
    63: "Moderate rain",
    65: "Heavy rain",
    66: "Light freezing rain",
    67: "Heavy freezing rain",
    71: "Slight snow",
    73: "Moderate snow",
    75: "Heavy snow",
    77: "Snow grains",
    80: "Slight rain showers",
    81: "Moderate rain showers",
    82: "Violent rain showers",
    85: "Slight snow showers",
    86: "Heavy snow showers",
    95: "Thunderstorm",
    96: "Thunderstorm with slight hail",
    99: "Thunderstorm with heavy hail",

    // Existing Met Office-ish compact mappings
    5: "Mist",
    6: "Fog",
    7: "Cloudy",
    8: "Overcast",
    9: "Light rain shower",
    10: "Light rain",
    11: "Drizzle",
    12: "Rain",
    13: "Heavy rain shower",
    14: "Heavy rain",
    15: "Sleet shower",
    16: "Sleet",
    17: "Hail shower",
    18: "Hail",
    19: "Light snow shower",
    20: "Light snow",
    21: "Heavy snow shower",
    22: "Heavy snow",
    23: "Thunder shower",
    24: "Thunder",
    25: "Light hail shower",
    26: "Light hail",
    27: "Heavy hail shower",
    28: "Heavy hail",
    29: "Thunder with hail",
    30: "Thunderstorm",
  };

  if (exactMap[c]) return exactMap[c];

  // Defensive fallbacks
  if (c >= 30 && c <= 39) return "Thunderstorm";
  if (c >= 20 && c <= 29) return "Showers";
  if (c >= 80 && c <= 82) return "Rain showers";
  if (c >= 85 && c <= 86) return "Snow showers";
  if (c >= 95) return "Thunderstorm";

  return `Weather code ${c}`;
};

const emojiForWeatherCode = (code) => {
  const c = Number(code);
  if (!Number.isFinite(c)) return "🌤️";

  // Open-Meteo / WMO first
  if (c === 0) return "☀️";
  if (c === 1 || c === 2) return "🌤️";
  if (c === 3) return "☁️";
  if (c === 45 || c === 48) return "🌫️";
  if (c === 51 || c === 53 || c === 55 || c === 56 || c === 57) return "🌦️";
  if (c === 61 || c === 63 || c === 65 || c === 66 || c === 67) return "🌧️";
  if (c === 71 || c === 73 || c === 75 || c === 77) return "❄️";
  if (c === 80 || c === 81 || c === 82) return "🌦️";
  if (c === 85 || c === 86) return "🌨️";
  if (c === 95 || c === 96 || c === 99) return "⛈️";

  // Existing Met Office-ish compact codes
  if (c >= 30 && c <= 39) return "⛈️";
  if (c === 18 || c === 19 || c === 20 || c === 21 || c === 22) return "❄️";
  if (c === 15 || c === 16 || c === 17) return "🌨️";
  if (c === 9 || c === 10 || c === 11 || c === 12 || c === 13 || c === 14) return "🌧️";
  if (c === 5 || c === 6) return "🌫️";
  if (c === 7 || c === 8) return "☁️";
  if (c === 0 || c === 1) return "☀️";
  if (c === 2 || c === 3) return "🌤️";

  return "🌤️";
};

export default function ExpectedWeatherCard({
  title = "Expected Weather",
  lat,
  lon,
  locationLabel = "",
  jobStart,
  jobEnd,
  className = "",
}) {
  const startDate = useMemo(() => ymdFromAny(jobStart), [jobStart]);
  const endDateFromJob = useMemo(() => ymdFromAny(jobEnd), [jobEnd]);
  const days = useMemo(() => daysInclusive(jobStart, jobEnd), [jobStart, jobEnd]);

  // If jobEnd is missing, derive an end date from start + (days-1)
  const effectiveEndDate = useMemo(() => {
    if (endDateFromJob) return endDateFromJob;
    if (!startDate) return "";
    return addDaysYmd(startDate, Math.max(0, Number(days || 1) - 1));
  }, [endDateFromJob, startDate, days]);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const doLoad = async () => {
      setErr("");
      setRows([]);

      const la = Number(lat);
      const lo = Number(lon);
      if (!Number.isFinite(la) || !Number.isFinite(lo)) return;
      if (!startDate) return;

      setLoading(true);
      try {
        const r = await axiosInstance.get("weather/spot", {
          params: {
            lat: la,
            lon: lo,
            period: "daily",
            provider: "auto",
            start_date: startDate,
            days,
          },
        });

        if (cancelled) return;

        const data = r?.data || {};
        const series = Array.isArray(data?.series) ? data.series : [];

        // Backend can return days before start_date; filter strictly by job range.
        const sKey = startDate;
        const eKey = effectiveEndDate || startDate;

        const filtered = series
          .map((x) => {
            const t = String(x?.time || "");
            const key = t.slice(0, 10); // YYYY-MM-DD from ISO or date string
            return { ...x, _dayKey: key };
          })
          .filter((x) => {
            const k = x?._dayKey || "";
            if (!/^\d{4}-\d{2}-\d{2}$/.test(k)) return false;
            return k >= sKey && k <= eKey;
          });

        setRows(filtered);
      } catch (e) {
        if (cancelled) return;

        const status = e?.response?.status;
        if (status === 404) {
          setErr("Not Found");
          setRows([]);
          return;
        }

        setErr(extractHttpError(e));
        setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    doLoad();
    return () => {
      cancelled = true;
    };
  }, [lat, lon, startDate, effectiveEndDate, days]);

  const headerLine = useMemo(() => {
    const endStr = effectiveEndDate || startDate || "—";
    const loc = locationLabel ? ` · ${locationLabel}` : "";
    return `${startDate || "—"} → ${endStr}${loc}`;
  }, [effectiveEndDate, locationLabel, startDate]);

  const bgStyle = {
    background:
      "linear-gradient(135deg, var(--primary-color, #111827), var(--secondary-color, #374151))",
    borderColor: "rgba(255,255,255,0.08)",
  };

  return (
    <div className={`mt-4 min-w-0 rounded-xl border overflow-hidden ${className}`} style={bgStyle}>
      <div className="flex flex-col gap-1.5 px-3 py-3 sm:flex-row sm:items-start sm:justify-between sm:px-4">
        <div className="min-w-0 flex-1">
          <div
            className="text-sm font-semibold break-words"
            style={{ color: "var(--accent-color)" }}
          >
            {title}
          </div>
          <div
            className="text-xs leading-5 break-words"
            style={{ color: "rgba(255,255,255,0.75)" }}
          >
            {headerLine}
          </div>
        </div>

        <div
          className="shrink-0 text-xs sm:pt-0.5"
          style={{ color: "rgba(255,255,255,0.7)" }}
        >
          {loading ? "Loading…" : rows?.length ? `${rows.length} day(s)` : ""}
        </div>
      </div>

      <div className="px-3 pb-3 sm:px-4 sm:pb-4">
        {err ? (
          <div className="text-xs font-semibold" style={{ color: "#fecaca" }}>
            ❌ {err}
          </div>
        ) : !Number.isFinite(Number(lat)) || !Number.isFinite(Number(lon)) ? (
          <div className="text-xs" style={{ color: "rgba(255,255,255,0.75)" }}>
            Set a location with coordinates to enable forecast.
          </div>
        ) : !startDate ? (
          <div className="text-xs" style={{ color: "rgba(255,255,255,0.75)" }}>
            Set Scheduled Start to enable forecast.
          </div>
        ) : loading ? (
          <div className="text-xs" style={{ color: "rgba(255,255,255,0.75)" }}>
            Loading forecast…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-xs" style={{ color: "rgba(255,255,255,0.75)" }}>
            No forecast returned.
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((d, idx) => {
              const dayKey = d?._dayKey || String(d?.time || "").slice(0, 10);
              const code = d?.weather_code ?? null;

              const summary = labelForWeatherCode(code) || "—";
              const tMax = d?.day_max_temp_c ?? null;
              const tMin = d?.night_min_temp_c ?? null;

              const gustMph = mpsToMph(d?.wind_gust_mps);
              const popDay = d?.day_precip_prob_pct;
              const popNight = d?.night_precip_prob_pct;
              const pop =
                Number.isFinite(Number(popDay)) && Number.isFinite(Number(popNight))
                  ? Math.max(Number(popDay), Number(popNight))
                  : Number.isFinite(Number(popDay))
                  ? Number(popDay)
                  : Number.isFinite(Number(popNight))
                  ? Number(popNight)
                  : null;

              return (
                <div
                  key={`ew-${dayKey || idx}`}
                  className="flex min-w-0 items-start gap-2.5 rounded-lg px-2.5 py-2.5 sm:items-center sm:gap-3 sm:px-3 sm:py-2"
                  style={{ backgroundColor: "rgba(255,255,255,0.06)" }}
                >
                  <div
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg sm:h-10 sm:w-10"
                    style={{ backgroundColor: "rgba(0,0,0,0.25)" }}
                    aria-hidden
                  >
                    <span style={{ fontSize: 20 }}>{emojiForWeatherCode(code)}</span>
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
                      <div
                        className="min-w-0 flex-1 text-sm font-semibold break-words"
                        style={{ color: "rgba(255,255,255,0.92)" }}
                      >
                        {fmtDay(dayKey)}
                      </div>
                      <div
                        className="shrink-0 text-sm font-semibold"
                        style={{ color: "rgba(255,255,255,0.92)" }}
                      >
                        {tMax != null || tMin != null ? (
                          <>
                            {tMax != null && Number.isFinite(Number(tMax)) ? `${Math.round(Number(tMax))}°` : "—"}
                            {tMin != null && Number.isFinite(Number(tMin))
                              ? ` / ${Math.round(Number(tMin))}°`
                              : ""}
                          </>
                        ) : (
                          "—"
                        )}
                      </div>
                    </div>

                    <div
                      className="mt-0.5 text-xs leading-5 break-words"
                      style={{ color: "rgba(255,255,255,0.72)" }}
                    >
                      {summary}
                      {pop != null ? ` · ${Math.round(Number(pop))}%` : ""}
                      {gustMph != null ? ` · Gust ${Math.round(Number(gustMph))} mph` : ""}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}